import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import { useMemo } from 'react';
import type { FullMapListViewStrings } from '@/components/maps/list-view/FullMapListView';
import type { ShoppingCartItem } from '@/models/ShoppingCartItem';
import type { ImageProps } from 'next/image';
import { getLocation } from '@/utils/getVenueLocation';
import { formatMessage } from '@/utils/translationHelper';
import placeholder from '@/assets/images/placeholder.png';
import ImageComponent from '@/components/ImageComponent';
import type { Locale } from '@/i18n';
import RatingReviewDistance from '@/components/card/RatingReviewDistance';
import RequestQuoteButton from '@/components/RequestQuoteButton';
import VenueHighlights from '@/components/card/VenueHighlights';
import { Shimmer, toBase64 } from '@/components/skeletons/ImageSkeleton';
import type { MeasurementUnit } from '@cvent/venue-search-shared/types';

type DiamondPlusAdProps = {
  venue: CsnSearchVenue;
  adTagline?: string;
  venueIndex: number;
  locale: Locale;
  isLoggedIn: boolean;
  activeFilters: string;
  isRFPFullAccess: boolean;
  fullMapListViewStrings: FullMapListViewStrings;
  shoppingCartItem?: ShoppingCartItem;
  measurementUnit: MeasurementUnit;
};

export default function FullMapDiamondPlusAd({
  venue,
  adTagline,
  venueIndex,
  locale,
  isLoggedIn,
  activeFilters,
  isRFPFullAccess,
  fullMapListViewStrings,
  shoppingCartItem,
  measurementUnit
}: DiamondPlusAdProps): JSX.Element {
  const {
    ratingAndReviewStrings,
    requestQuoteButtonStrings,
    venueImageAltText,
    screenReaderText,
    venueHighlightStrings
  } = fullMapListViewStrings;

  const isMicrosite = venue.listingOwnerType === 'MICROSITE';
  const isMicrositeDiamondPlus = Boolean(isMicrosite && venue.featuredLevel === 'DIAMOND_PLUS');

  const venueLocation = useMemo(
    () => getLocation(venue.city, venue.stateProvinceCode, venue.countryCode),
    [venue.city, venue.countryCode, venue.stateProvinceCode]
  );

  let imgAltText = venue.name;
  if (venueLocation) {
    imgAltText = formatMessage(venueImageAltText, locale, {
      venueName: venue.name,
      venueLocation
    });
  }
  const venueImageSrc = useMemo(
    () =>
      venue.mainImage?.large ??
      venue.mainImage?.original ??
      venue.mainImage?.medium ??
      venue.mainImage?.small ??
      venue.mainImage?.extraSmall ??
      venue.firstProfileImageUrl,
    [
      venue.firstProfileImageUrl,
      venue.mainImage?.extraSmall,
      venue.mainImage?.large,
      venue.mainImage?.medium,
      venue.mainImage?.original,
      venue.mainImage?.small
    ]
  );

  const imageProps: ImageProps = {
    fill: true,
    sizes: '100vw',
    style: { objectFit: 'cover' },
    src: venue.displaySearchResultImage && venueImageSrc ? venueImageSrc : placeholder.src,
    alt: imgAltText,
    placeholder: `data:image/svg+xml;base64,${toBase64(Shimmer(384, 192))}`
  };

  return (
    <div
      className="my-4 flex min-h-[320px] w-full flex-col rounded-lg bg-hb-background shadow-2"
      data-cvent-id={`list-view-venue-card-full-map-${venue.id}`}
    >
      <div className="w-full">
        <div className="absolute z-10 rounded-br-md rounded-tl-md bg-warning-10 px-2 py-0.5 text-b-2xs font-bold">
          {formatMessage(fullMapListViewStrings.promoted, locale)}
        </div>
        <div className="relative h-48 w-full flex-shrink-0 overflow-hidden rounded-tl-md rounded-tr-md">
          <ImageComponent imageProps={imageProps} />
        </div>
      </div>
      <div className="flex h-full w-full flex-col justify-between gap-2 overflow-hidden p-2">
        <div className="flex justify-between">
          <div className="w-5/6">
            <h3 className="line-clamp-2 break-words text-b-base/normal text-hb-brand-60" title={venue.name}>
              {venue.name}
            </h3>
          </div>
          {shoppingCartItem && !isMicrositeDiamondPlus && (
            <div>
              <RequestQuoteButton
                activeFilters={activeFilters}
                strings={requestQuoteButtonStrings}
                item={shoppingCartItem}
                iconOnly
                className="!h-6 !min-h-0 !w-6 !rounded-sm"
                isRFPFullAccess={isRFPFullAccess}
                displayTypes={venue.displayTypes}
                isLoggedIn={isLoggedIn}
                testID={`full-map-venue-container-${venueIndex + 1}`}
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <RatingReviewDistance
            ratings={venue.preferredRating}
            reviews={venue.plannerReviewsRating}
            screenReaderText={screenReaderText}
            locale={locale}
            strings={ratingAndReviewStrings}
            isListViewInFullMap
          />
          {venue.preferredRating && venue.totalMeetingRoom ? (
            <div className="text-b-md/tight text-neutral-60">|</div>
          ) : null}
          <VenueHighlights
            venue={venue}
            measurementUnit={measurementUnit}
            locale={locale}
            highlightStrings={venueHighlightStrings}
            isMapFlyout
          />
        </div>
        <span className="font-primary text-b-sm">{adTagline}</span>
      </div>
    </div>
  );
}
